<template>
  <div class="page-container">
    <div class="header">
      <span class="rover-name">{{ currentRover.name }}</span>
      <b-button
        class="lone-button"
        pill
        variant="danger"
        @click.prevent="endSession"
        size="sm">
        END SESSION
      </b-button>
      <span class="time-display">Time: {{ formattedTime }}</span>
    </div>

    <div id="appstream-container" :style="`height: ${adjustedViewportHeight}`"></div>
  </div>
</template>


<script>
import { getInstance } from "@/auth/index";
import Jobs from "@/services/api/controllers/jobs";
import Sessions from "@/services/api/controllers/sessions";
import Rovers from "@/services/api/controllers/rovers";

export default {
  beforeDestroy() {
    window.removeEventListener(
      "beforeunload",
      this.handleNavigationAwayFromPage
    );

    if (this.ticker) {
      this.ticker.stop();
    }

    return;
  },

  beforeMount() {
    window.addEventListener("beforeunload", this.handleNavigationAwayFromPage);

    // Every time the window resizes, we get a new inner height to be 90% of the viewport height
    // We use this instead of vh property because Safari and iOS devices are extremely buggy when using vh.
    window.addEventListener("resize", () => {
      this.adjustedViewportHeight = `${window.innerHeight * 0.9}px`;
    });

    // Set page-container height for the initial load.
    this.adjustedViewportHeight = `${window.innerHeight * 0.9}px`;

    return;
  },

  async beforeRouteLeave(to, from, next) {
    try {
      // If we end the session on the front-end, immediately run our job such that the session is ended and the rover is freed ASAP
      if (this.avoidRouteLeave && !this.navigationError) {
        console.log("CASE 1");
        window.alert(
          "Your robot session has ended, did you run out of credits?"
        );
        const accessToken = await this.$auth.getTokenSilently();
        console.log("AFTER TOKEN");
        await Jobs.create(this.currentRover.id, accessToken);
        console.log("AFTER JOBS");
        if (this.started) {
          let externalScript = document.createElement("script");
          externalScript.setAttribute(
            "src",
            "https://teleport.world/trigger.php?a=stop"
          );
          document.head.appendChild(externalScript);
          this.started = false;
        }
        next();
      } else if (this.navigationError) {
        // In the event that the user catastrophically causes an error on the client side, send them straight to the sessions screen. The handler will have to pick and end the session that catastrophically failed
        console.log("CASE 2");
        window.alert(
          "An error has occured with your robot session, please try again later."
        );
        if (this.started) {
          let externalScript = document.createElement("script");
          externalScript.setAttribute(
            "src",
            "https://teleport.world/trigger.php?a=stop"
          );
          document.head.appendChild(externalScript);
          this.started = false;
        }

        next();
      } else {
        console.log("CASE 3");
        const answer = window.confirm(
          "Are you sure you want to leave? This will terminate the session."
        );

        if (answer) {
          const accessToken = await this.$auth.getTokenSilently();
          await Jobs.create(this.currentRover.id, accessToken);
          if (this.started) {
            let externalScript = document.createElement("script");
            externalScript.setAttribute(
              "src",
              "https://teleport.world/trigger.php?a=stop"
            );
            document.head.appendChild(externalScript);
            this.started = false;
          }

        next();
        } else {
          next(false);
        }
      }
    } catch (error) {
      console.log("Failed to leave route, what is going on?ß");
      console.log(error);

      // Still navigate away from the page to get the session information
      this.navigationError = true;
      this.$router.push({
        path: `/sessions/${this.$route.params.sessionID}/${this.$route.params.id}`
      });
      console.log("Error 69");
    }
  },

  data: function() {
    return {
      adjustedViewportHeight: "500px",

      appStreamObject: Object,

      avoidRouteLeave: false,

      currentRover: [],

      hours: 0,

      minutes: 0,

      navigationError: false,

      pageReady: true,

      started: false,

      seconds: 0,

      time: 0,

      youtubeStreamURL: String
    };
  },

  computed: {
    formattedTime() {
      const pad = (number) => number.toString().padStart(2, '0');
      return `${pad(this.hours)}:${pad(this.minutes)}:${pad(this.seconds)}`;
    }
  },

  async mounted() {
    try {
      this.pageReady = false;

      // Grab correct rover information
      this.currentRover = await Rovers.show(this.$route.params.id);

      // We only want to initialize the AppStreamObject if the user is authenticated
      if (
        // Case 1: User is authenticated
        this.$auth.isAuthenticated
      ) {
        this.initializeAppStreamObject();
      } else if (!this.$auth.isAuthenticated) {
        // Case 2: User is not authenticated
        const authService = getInstance();
        authService.loginWithRedirect({
          appState: { targetUrl: `/control/robots/${this.$route.params.id}` }
        });
      }
    } catch (error) {
      console.log("Failed to mount");
      console.log(error);
    } finally {
      this.pageReady = true;
      console.log("Page Ready");
    }
  },

  methods: {
    endSession: async function() {
      try {
        this.$router.push({
          path: `/sessions/${this.$route.params.sessionID}/${this.$route.params.id}`
        });
        this.appStreamObject.endSession();
        console.log("endSession Fuction Executed");
      } catch (error) {
        console.log("Failed to gracefully end session");
        console.log(error);
      }
    },

    goToRovers: function() {
      this.$router.push({ path: "/robots" });
    },

    handleNavigationAwayFromPage: async function(event) {
      try {
        event.preventDefault();

        this.$router.push({
          path: `/sessions/${this.$route.params.sessionID}/${this.$route.params.id}`
        });
        console.log("HandleNavigationAwayFromPage Function Executed");
      } catch (error) {
        console.log("Navigation away from page error");
        console.log(error);
      }
    },

    incrementTime: function() {
      this.time = parseInt(this.time) + 1;

      this.hours = Math.floor(this.time / 3600);
      this.minutes = Math.floor((this.time - this.hours * 3600) / 60);
      this.seconds = this.time - this.hours * 3600 - this.minutes * 60;

      return;
    },

    initializeAppStreamObject: async function() {
      try {
        let hiddenElements = [];

        // Grab AppStream URL that belongs to our user
        const accessToken = await this.$auth.getTokenSilently();
        const session = await Sessions.show(this.$route.params.id, accessToken);

        if (!session.streaming_url) {
          throw "No session URL exists that is active or is for the current user";
        }

        let appstreamOptions = {
          sessionURL: session.streaming_url,
          userInterfaceConfig: {
            [AppStream.Embed.Options.HIDDEN_ELEMENTS]: [
              AppStream.Embed.Elements.TOOLBAR
            ]
          }
        };

        document.getElementById("appstream-container").style.visibility =
          "visible";

        this.appStreamObject = await new AppStream.Embed(
          "appstream-container",
          appstreamOptions
        );

        // Add a listener to watch the AppStream State
        this.appStreamObject.addEventListener(
          AppStream.Embed.Events.SESSION_STATE_CHANGE,
          this.handleSessionChange
        );

        return;
      } catch (error) {
        console.log("Failed to initialize App Stream Object");
        console.log(error);

        throw error;
      }
    },

    /**
     * If the embed object successfully indicates the user session has started, we will run this callback to get the session information
     */
    handleSessionChange: async function(event) {
      try {
        const { sessionStatus } = event;

        const ticker = new this.handleWhileAdjustingIntervals(
          this.incrementTime,
          1000
        );
        if (this.ticker) {
          this.ticker.stop();
        }
        this.ticker = ticker;

        switch (sessionStatus) {
          case "Started":
            // Set Interval for the counter
            console.log("AppStream Session Started");
            if (this.currentRover.type === "Rover") {
              let externalScript = document.createElement("script");
              externalScript.setAttribute(
                "src",
                "https://teleport.world/trigger.php?a=start"
              );
              document.head.appendChild(externalScript);
              this.started = true;
            }
            this.ticker.start();

            // Update the session start time to be accurate
            var accessToken = await this.$auth.getTokenSilently();
            await Sessions.updateSessionStart(
              this.$route.params.id,
              accessToken
            );

            break;
          case "Disconnected":
            // End the session
            console.log("AppStream Session Disconnected");
            console.log(event)
            this.avoidRouteLeave = true;
            this.endSession();

            break;
          default:
            break;
        }
      } catch (error) {
        console.log("FAILED TO HANDLE SESSION CHANGE");
        console.log(error);
      }
    },

    /**
     * Self-adjusting interval to account for drifting
     *
     * @param {function} workFunc  Callback containing the work to be done
     *                             for each interval
     * @param {int}      interval  Interval speed (in milliseconds) - This
     * @param {function} errorFunc (Optional) Callback to run if the drift
     *                             exceeds interval
     */
    handleWhileAdjustingIntervals: function(workFunc, interval, errorFunc) {
      var that = this;
      var expected, timeout;
      this.interval = interval;

      this.start = function() {
        expected = Date.now() + this.interval;
        timeout = setTimeout(step, this.interval);
      };

      this.stop = function() {
        clearTimeout(timeout);
      };

      function step() {
        var drift = Date.now() - expected;
        if (drift > that.interval) {
          // If you want to call an error callback when drift is greater than interval
          if (errorFunc) errorFunc();
        }
        workFunc();
        expected += that.interval;
        // Set the next timeout to account for the drift
        timeout = setTimeout(step, Math.max(0, that.interval - drift));
      }
    }
  }
};
</script>

<style scoped>
#appstream-container {
  border: none;
  display: flex;
  flex-grow: 1;
  width: 100vw;
  max-width: 100%;
  overflow-y: auto;
  overflow-x: auto;
}

.page-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%; /* Ensure the container takes full width */
}

.header {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%; /* Full width to center content */
  gap: 0.5rem; /* Spacing between items */
}

.rover-name, .time-display {
  flex-grow: 1;
  text-align: center;
}

.lone-button {
  margin-top: 0.5rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  margin-left: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
}

</style>
